// screens/LoginScreen.js
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useLang } from '../contexts/LanguageContext';
import { useSync } from '../contexts/SyncContext';
import { getErrorMessage } from '../utils/api';
import { OfflineLoginError } from '../utils/offlineAuth';
import ThemeToggle from '../components/ThemeToggle';
import LanguageToggle from '../components/LanguageToggle';

const OFFLINE_ERROR_KEYS = {
  'unknown-user': 'login.offline.unknown',
  'wrong-password': 'login.offline.wrong',
  locked: 'login.offline.wait',
  unsupported: 'login.offline.unsupported',
  expired: 'login.offline.expired',
};

function offlineErrorText(err, t) {
  const seconds = Math.max(1, Math.ceil(err.retryAfterMs / 1000));
  return t(OFFLINE_ERROR_KEYS[err.code]).replace('{seconds}', seconds);
}

export default function LoginScreen() {
  const { login, sessionNotice } = useAuth();
  const { isOnline } = useSync();
  const toast = useToast();
  const { t } = useLang();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPw, setShowPw] = useState(false);
  const isStandalone = typeof window !== 'undefined' &&
    window.matchMedia?.('(display-mode: standalone)').matches;

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username || !password) { setError(t('login.enterboth')); return; }
    setError('');
    setLoading(true);
    try {
      await login(username.trim(), password);
    } catch (err) {
      setError(err instanceof OfflineLoginError ? offlineErrorText(err, t) : getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', minHeight: '100dvh',
      background: 'var(--bg-primary)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '24px',
      position: 'relative', overflow: 'hidden'
    }}>
      {/* Theme + language toggle, top right */}
      <div style={{ position: 'absolute', top: 16, right: 16, zIndex: 2, display: 'flex', gap: 4 }}>
        <LanguageToggle />
        <ThemeToggle />
      </div>

      {/* Background decoration */}
      <div style={{
        position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none',
        zIndex: 0
      }}>
        <div style={{
          position: 'absolute', top: -200, right: -200,
          width: 600, height: 600,
          background: 'radial-gradient(circle, rgba(79,142,247,0.08) 0%, transparent 70%)',
          borderRadius: '50%'
        }} />
        <div style={{
          position: 'absolute', bottom: -200, left: -200,
          width: 500, height: 500,
          background: 'radial-gradient(circle, rgba(124,106,247,0.06) 0%, transparent 70%)',
          borderRadius: '50%'
        }} />
      </div>

      <div style={{
        width: '100%', maxWidth: 400, position: 'relative', zIndex: 1,
        animation: 'slideUp 0.3s ease'
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 72, height: 72, borderRadius: 20,
            background: 'linear-gradient(135deg, var(--accent-primary), var(--accent-secondary))',
            marginBottom: 16, fontSize: '2rem'
          }}>📷</div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 800, letterSpacing: '-0.02em' }}>PhotoApp</h1>
          <p style={{ color: 'var(--text-muted)', marginTop: 6 }}>{t('login.subtitle')}</p>
        </div>

        {/* Form */}
        <div className="card" style={{ padding: 28 }}>
          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div className="form-group">
              <label className="form-label">{t('login.username')}</label>
              <input
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                placeholder={t('login.username.ph')}
                className={`input-field ${error ? 'error' : ''}`}
                autoCapitalize="none"
                autoCorrect="off"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('login.password')}</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={t('login.password.ph')}
                  className={`input-field ${error ? 'error' : ''}`}
                  style={{ paddingRight: 48 }}
                />
                <button type="button" onClick={() => setShowPw(!showPw)} style={{
                  position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)',
                  fontSize: '1rem', padding: 0
                }}>{showPw ? '🙈' : '👁'}</button>
              </div>
            </div>

            {!error && sessionNotice === 'rejected' && (
              <div className="error-banner">
                <span>⚠</span>
                <span>{t('login.session.rejected')}</span>
              </div>
            )}

            {!error && !isOnline && (
              <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', margin: 0 }}>
                {t('login.offline.hint')}
              </p>
            )}

            {error && (
              <div className="error-banner">
                <span>⚠</span>
                <span>{error}</span>
              </div>
            )}

            <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={loading}>
              {loading ? <><div className="spinner" style={{ width: 16, height: 16 }} /> {t('login.signingin')}</> : t('login.signin')}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <button style={{
              background: 'none', border: 'none', color: 'var(--accent-primary)',
              cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500
            }} onClick={() => toast.info(t('login.forgot.msg'))}>
              {t('login.forgot')}
            </button>
          </div>
        </div>

        <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: 24 }}>
          {t('login.secure')}
        </p>

        {!isStandalone && (
          <p style={{ textAlign: 'center', marginTop: 12 }}>
            <a href="/download" style={{ color: 'var(--accent-primary)', fontSize: '0.8rem', textDecoration: 'none', fontWeight: 500 }}>
              📱 {t('login.install')}
            </a>
          </p>
        )}
      </div>
    </div>
  );
}
